import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import {
  MessageActivityClassName,
  AutoScrollerContentClassName,
  AIMessageClassName,
  ForceIntoVisibilityClassName,
  ActivityGroupContainerClassName,
} from '../utils';

interface IUserAutoToTopScrollProps {
  isStartPointOfNewConversation: boolean;
  autoScrollContainerRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
  isScrollerEnabled: boolean;
  isCustomStream?: boolean;
}

export const useAutoToTopScroll = (props: IUserAutoToTopScrollProps) => {
  const {
    isStartPointOfNewConversation,
    containerRef,
    autoScrollContainerRef,
    isScrollerEnabled,
    isCustomStream,
  } = props;

  const hasPaddingRef = useRef(false);
  const currentPaddingRef = useRef(0);
  const previousIsStartPointRef = useRef(isStartPointOfNewConversation);

  const removePaddingTimeout = useCallback(() => {
    if (previousIsStartPointRef.current) {
      setTimeout(removePaddingTimeout, 1000);
    } else {
      const containerEl = containerRef.current;
      const autoScrollContainer = autoScrollContainerRef.current;
      const contentContainer = containerRef.current?.querySelector(`.${AutoScrollerContentClassName}`) as HTMLElement;

      if (contentContainer && hasPaddingRef.current && containerEl && autoScrollContainer) {
        // Find the last AI message and check if its height is higher than container height
        const allAiMessages = autoScrollContainer.querySelectorAll(`.${AIMessageClassName}, .${ForceIntoVisibilityClassName}`);
        const lastAiMessage = allAiMessages.length > 0
          ? allAiMessages[allAiMessages.length - 1]
          : null;

        // Only remove padding if last AI message height is greater than container height
        // This prevents jumping when there's not enough content to fill the window
        if (lastAiMessage) {
          const aiMessageHeight = lastAiMessage.getBoundingClientRect().height;
          if (aiMessageHeight > containerEl.clientHeight) {
            contentContainer.style.paddingBottom = `${currentPaddingRef.current}px`;
            hasPaddingRef.current = false;
          }
        }
      }
    }
  }, [containerRef, autoScrollContainerRef]);

  const removePaddingOnWheel = useCallback(() => {
    const contentContainer = containerRef.current?.querySelector(`.${AutoScrollerContentClassName}`) as HTMLElement;
    if (contentContainer && hasPaddingRef.current) {
      contentContainer.style.paddingBottom = `${currentPaddingRef.current}px`;
      hasPaddingRef.current = false;
    }
  }, [containerRef]);

  const isHumanMessageFlowIn = useMemo(() => {
    const isHumanMsg = isStartPointOfNewConversation && !previousIsStartPointRef.current;
    previousIsStartPointRef.current = isStartPointOfNewConversation;
    return isHumanMsg;
  }, [isStartPointOfNewConversation]);

  // Add wheel event listener to remove padding on user scroll
  useEffect(() => {
    if (!isScrollerEnabled || isCustomStream) {
      return;
    }

    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    containerEl.addEventListener('wheel', removePaddingOnWheel);

    // eslint-disable-next-line consistent-return
    return () => {
      containerEl.removeEventListener('wheel', removePaddingOnWheel);
    };
  }, [isScrollerEnabled, isCustomStream, containerRef, removePaddingOnWheel]);

  // Track when we're expecting a new human message and scroll last AI message out of view
  useEffect(() => {
    if (!isScrollerEnabled || isCustomStream) {
      return;
    }

    const containerEl = containerRef.current;
    const autoScrollContainer = autoScrollContainerRef.current;

    if (!containerEl || !autoScrollContainer) {
      return;
    }

    if (isHumanMessageFlowIn) {
      // Scroll the last AI message to an invisible place above the visible area
      const allAiMessages = autoScrollContainer.querySelectorAll(`.${AIMessageClassName}, .${ForceIntoVisibilityClassName}`);
      const lastAiMessage = allAiMessages.length > 0
        ? allAiMessages[allAiMessages.length - 1]
        : null;

      if (lastAiMessage) {
        requestAnimationFrame(() => {
          const containerVisibleHeight = containerEl.clientHeight;
          const aiMessageRect = lastAiMessage.getBoundingClientRect();
          const containerRect = containerEl.getBoundingClientRect();

          // Calculate position to scroll AI message completely above the visible area
          const aiMessageAbsoluteTop = containerEl.scrollTop + (aiMessageRect.top - containerRect.top);
          const aiMessageHeight = aiMessageRect.height;

          // Get the content container
          const contentContainer = containerEl.querySelector(`.${AutoScrollerContentClassName}`) as HTMLElement;
          const adGroupContainer = containerEl.querySelector(`.${ActivityGroupContainerClassName}`) as HTMLElement;

          if (contentContainer) {
            // Check if there's unreleased padding from previous operation and remove it first
            if (hasPaddingRef.current) {
              contentContainer.style.paddingBottom = `${currentPaddingRef.current}px`;
              hasPaddingRef.current = false;
            }

            // Calculate how much content is below the AI message
            const contentBelowAiMessage = containerEl.scrollHeight - aiMessageAbsoluteTop;

            // If there's not enough content below to scroll the AI message out of view, add padding
            if (contentBelowAiMessage < containerVisibleHeight + aiMessageHeight) {
              let additionalHeightNeeded = (containerVisibleHeight + aiMessageHeight) - contentBelowAiMessage;
              if (adGroupContainer && adGroupContainer.clientHeight < containerVisibleHeight) {
                additionalHeightNeeded = containerVisibleHeight;
              }

              // Save the original padding (get fresh value since we may have just reset it)
              const currentPadding = parseInt(window.getComputedStyle(contentContainer).paddingBottom, 10) || 0;
              currentPaddingRef.current = currentPadding;

              // Set padding: original padding + additional height needed
              const newPadding = currentPaddingRef.current + additionalHeightNeeded;
              contentContainer.style.paddingBottom = `${newPadding}px`;
              hasPaddingRef.current = true;

              // Wait for padding to take effect, then scroll
              requestAnimationFrame(() => {
                // Scroll so the AI message is just above the visible area
                const targetScrollTop = aiMessageAbsoluteTop + aiMessageHeight;
                containerEl.scrollTo({
                  top: targetScrollTop,
                  behavior: 'smooth',
                });
                setTimeout(removePaddingTimeout, 3000);
              });
            } else {
              // Enough content exists, just scroll
              const targetScrollTop = aiMessageAbsoluteTop + aiMessageHeight;
              containerEl.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth',
              });
            }
          }
        });
      }
    }
  }, [isHumanMessageFlowIn, isScrollerEnabled, containerRef, autoScrollContainerRef, isStartPointOfNewConversation, isCustomStream, removePaddingTimeout]);

  // when live agent stream is in, scroll all new message to view, including agent message and human message
  useEffect(() => {
    if (!isCustomStream || !isScrollerEnabled) {
      return;
    }

    const containerEl = containerRef.current;
    const autoScrollContainer = autoScrollContainerRef.current;

    if (!containerEl || !autoScrollContainer) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      if (!mutations || mutations.length === 0) {
        return;
      }

      // Look for new message-activity elements
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              // Check if the node or any of its children have message-activity class
              const messageActivityElements: HTMLElement[] = [];

              if (node.className && typeof node.className === 'string' && node.className.includes(MessageActivityClassName)) {
                messageActivityElements.push(node);
              }

              const childElements = node.querySelectorAll(`.${MessageActivityClassName}`);
              childElements.forEach((el) => {
                if (el instanceof HTMLElement) {
                  messageActivityElements.push(el);
                }
              });

              // Scroll the last message-activity element into view
              if (messageActivityElements.length > 0) {
                const lastElement = messageActivityElements[messageActivityElements.length - 1];
                requestAnimationFrame(() => {
                  lastElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
              }
            }
          });
        }
      });
    });

    observer.observe(autoScrollContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [isCustomStream, containerRef, autoScrollContainerRef, isScrollerEnabled]);

  // Handle ForceIntoVisibilityClassName: scroll into view when it's the final component
  useEffect(() => {
    if (!isScrollerEnabled || isCustomStream) {
      return;
    }

    const containerEl = containerRef.current;
    const autoScrollContainer = autoScrollContainerRef.current;

    if (!containerEl || !autoScrollContainer) {
      return;
    }

    let knownForceVisibilityElements = new Set(Array.from(autoScrollContainer.querySelectorAll(`.${ForceIntoVisibilityClassName}`)));

    const observer = new MutationObserver(() => {
      const currentElements = autoScrollContainer.querySelectorAll(`.${ForceIntoVisibilityClassName}`);

      // Find truly new elements (not previously seen)
      const newElements = Array.from(currentElements).filter(el => !knownForceVisibilityElements.has(el));
      knownForceVisibilityElements = new Set(Array.from(currentElements));

      if (newElements.length === 0) return;

      // Only scroll the new element if it's the final child in the container
      const newElement = newElements[newElements.length - 1];
      const allChildren = Array.from(autoScrollContainer.children);
      const lastChild = allChildren[allChildren.length - 1];

      if (lastChild && (lastChild === newElement || lastChild.contains(newElement) || newElement.contains(lastChild)) && !isHumanMessageFlowIn) {
        requestAnimationFrame(() => {
          newElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    });

    observer.observe(autoScrollContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [containerRef, autoScrollContainerRef, isScrollerEnabled, isCustomStream, isHumanMessageFlowIn]);
};
